/**
 * Validates Brazilian CPF or CNPJ document numbers
 * 
 * @param {string|number} value - The document number to validate
 * @param {Object} options - Optional configuration
 * @param {boolean} options.formatted - Whether to return formatted document (default: false)
 * @param {boolean} options.detailed - Whether to return detailed information (default: false)
 * @returns {boolean|Object} - Returns boolean if options.detailed is false, otherwise returns object with validation details
 */
export const validateBrazilianDocument = (value, options = {}) => {
  // Default options
  const config = {
    formatted: false,
    detailed: false,
    ...options
  };

  // Remove any non-digit characters
  const digits = value.toString().replace(/\D/g, '');
  
  // Initialize return object if detailed information is requested
  const result = config.detailed ? {
    isValid: false,
    type: null,
    value: digits,
    formatted: null,
    message: null
  } : false;
  
  // Determine document type based on length
  const documentType = digits.length === 11 ? 'CPF' : digits.length === 14 ? 'CNPJ' : null;
  
  // Check if length is valid
  if (!documentType) {
    if (config.detailed) {
      result.message = 'Document must have 11 (CPF) or 14 (CNPJ) digits';
      return result;
    }
    return false;
  }
  
  // Check for common invalid patterns (all same digits)
  if (/^(\d)\1+$/.test(digits)) {
    if (config.detailed) {
      result.type = documentType;
      result.message = `Invalid ${documentType}: repeated digits`;
      result.formatted = formatDocument(digits);
      return result;
    }
    return false;
  }
  
  // Set type for detailed result
  if (config.detailed) {
    result.type = documentType;
  }
  
  // Validate based on document type
  let isValid = false;
  
  if (documentType === 'CPF') {
    // CPF validation - First verification digit
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(digits.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    
    if (remainder !== parseInt(digits.charAt(9))) {
      if (config.detailed) {
        result.message = 'Invalid CPF: first verification digit is incorrect';
        result.formatted = formatDocument(digits);
        return result;
      }
      return false;
    }
    
    // CPF validation - Second verification digit
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(digits.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    
    isValid = remainder === parseInt(digits.charAt(10));
    
    if (!isValid && config.detailed) {
      result.message = 'Invalid CPF: second verification digit is incorrect';
      result.formatted = formatDocument(digits);
      return result;
    }
  } else {
    // CNPJ validation - First verification digit
    let size = digits.length - 2;
    let numbers = digits.substring(0, size);
    const verificationDigits = digits.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let remainder = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (remainder !== parseInt(verificationDigits.charAt(0))) {
      if (config.detailed) {
        result.message = 'Invalid CNPJ: first verification digit is incorrect';
        result.formatted = formatDocument(digits);
        return result;
      }
      return false;
    }
    
    // CNPJ validation - Second verification digit
    size += 1;
    numbers = digits.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    remainder = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    isValid = remainder === parseInt(verificationDigits.charAt(1));
    
    if (!isValid && config.detailed) {
      result.message = 'Invalid CNPJ: second verification digit is incorrect';
      result.formatted = formatDocument(digits);
      return result;
    }
  }
  
  // Handle successful validation
  if (config.detailed) {
    result.isValid = true;
    result.message = `Valid ${documentType}`;
    result.formatted = formatDocument(digits);
    return result;
  }
  
  // If formatted option is true, return the formatted string
  if (isValid && config.formatted) {
    return formatDocument(digits);
  }
  
  // Return boolean result
  return isValid;
  
  // Helper function to format the document
  function formatDocument(value) {
    if (value.length === 11) {
      // Format as CPF: 123.456.789-09
      return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (value.length === 14) {
      // Format as CNPJ: 12.345.678/0001-90
      return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return value;
  }
}